import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Award, Download, ExternalLink, Search, ShieldX } from 'lucide-react';
import { adminCertificadoService } from '../../shared/services';

const STATUS_LABEL: Record<string, string> = {
  valido: 'Válido',
  expirado: 'Expirado',
  revogado: 'Revogado',
};

function mensagemDeErro(error: unknown, padrao: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    if (data?.message) return data.message;
  }
  return padrao;
}

function formatarData(valor?: string | null) {
  if (!valor) return '—';
  return new Date(valor).toLocaleDateString('pt-BR');
}

export function AdminCertificadosPage() {
  const queryClient = useQueryClient();
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState('');
  const [tipo, setTipo] = useState('');
  const [page, setPage] = useState(1);
  const [aviso, setAviso] = useState<string | null>(null);
  const pageSize = 25;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-certificados', busca, status, tipo, page],
    queryFn: () => adminCertificadoService.listar({ busca, status, tipo, page, pageSize }),
  });

  const baixar = useMutation({
    mutationFn: ({ id, codigo }: { id: string; codigo: string }) => adminCertificadoService.baixarPdf(id, codigo),
    onError: (error) => setAviso(mensagemDeErro(error, 'Não foi possível baixar o PDF.')),
  });

  const revogar = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) => adminCertificadoService.revogar(id, motivo),
    onSuccess: () => {
      setAviso('Certificado revogado.');
      queryClient.invalidateQueries({ queryKey: ['admin-certificados'] });
    },
    onError: (error) => setAviso(mensagemDeErro(error, 'Não foi possível revogar o certificado.')),
  });

  const totalPaginas = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Award size={30} /> Certificados
          </h1>
          <p className="text-gray-600 mt-1">Consulte, baixe e revogue certificados emitidos.</p>
        </div>

        {aviso && <div className="mb-4 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-800">{aviso}</div>}

        <div className="card mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <label className="relative md:col-span-2">
            <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
            <input
              value={busca}
              onChange={(event) => {
                setBusca(event.target.value);
                setPage(1);
              }}
              placeholder="Buscar por nome, e-mail, título ou código"
              className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2"
            />
          </label>
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="">Todos os status</option>
            <option value="valido">Válidos</option>
            <option value="expirado">Expirados</option>
            <option value="revogado">Revogados</option>
          </select>
          <select
            value={tipo}
            onChange={(event) => {
              setTipo(event.target.value);
              setPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="">Todos os tipos</option>
            <option value="curso">Curso</option>
            <option value="trilha">Trilha</option>
          </select>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : isError || !data ? (
          <div className="card text-center py-10 text-red-600">Não foi possível carregar os certificados.</div>
        ) : (
          <section className="card overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4">Titular</th>
                  <th className="py-2 pr-4">Certificado</th>
                  <th className="py-2 pr-4">Empresa</th>
                  <th className="py-2 pr-4">Emissão</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((certificado) => (
                  <tr key={certificado.id} className="border-b border-gray-100">
                    <td className="py-3 pr-4">
                      <p className="font-medium text-gray-900">{certificado.usuarioNome}</p>
                      <p className="text-xs text-gray-500">{certificado.usuarioEmail}</p>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-gray-900">{certificado.titulo}</p>
                      <p className="text-xs text-gray-500 uppercase">{certificado.tipo}</p>
                      <p className="text-[10px] text-gray-400 font-mono mt-1">{certificado.codigo}</p>
                    </td>
                    <td className="py-3 pr-4 text-gray-600">{certificado.empresaNome ?? '—'}</td>
                    <td className="py-3 pr-4">
                      <p>{formatarData(certificado.emitidoEm)}</p>
                      <p className="text-xs text-gray-500">até {formatarData(certificado.validoAte)}</p>
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          certificado.status === 'valido'
                            ? 'bg-green-100 text-green-800'
                            : certificado.status === 'expirado'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {STATUS_LABEL[certificado.status] ?? certificado.status}
                      </span>
                      {certificado.motivoRevogacao && (
                        <p className="text-[10px] text-red-700 mt-1">{certificado.motivoRevogacao}</p>
                      )}
                    </td>
                    <td className="py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => baixar.mutate({ id: certificado.id, codigo: certificado.codigo })}
                        className="inline-flex items-center gap-1 text-primary-700 hover:underline mr-3"
                        title="Baixar PDF"
                      >
                        <Download size={14} /> PDF
                      </button>
                      <a
                        href={`/certificados/validar/${certificado.codigo}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-gray-600 hover:underline mr-3"
                        title="Validar publicamente"
                      >
                        <ExternalLink size={14} />
                      </a>
                      {certificado.status !== 'revogado' && (
                        <button
                          type="button"
                          onClick={() => {
                            const motivo = window.prompt('Motivo da revogação (mínimo 5 caracteres):');
                            if (!motivo || motivo.trim().length < 5) return;
                            if (window.confirm(`Revogar o certificado de ${certificado.usuarioNome}?`)) {
                              revogar.mutate({ id: certificado.id, motivo });
                            }
                          }}
                          className="inline-flex items-center gap-1 text-red-600 hover:underline"
                          title="Revogar certificado"
                        >
                          <ShieldX size={14} /> Revogar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {data.items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500">Nenhum certificado encontrado.</td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
              <span>{data.total} certificado(s)</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((valor) => Math.max(1, valor - 1))}
                  disabled={page <= 1}
                  className="btn-secondary disabled:opacity-50"
                >
                  Anterior
                </button>
                <span>Página {page} de {totalPaginas}</span>
                <button
                  type="button"
                  onClick={() => setPage((valor) => Math.min(totalPaginas, valor + 1))}
                  disabled={page >= totalPaginas}
                  className="btn-secondary disabled:opacity-50"
                >
                  Próxima
                </button>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
